import { Inject, Injectable, Optional } from "@angular/core";
import { JwtHelper, LocalstorageHelper, LogService } from "@nts/std/utility";
import { distinctUntilChanged, Subject } from "rxjs";
import { AUTH_MOCK_MODE, DecodedTokenInterface } from "@nts/std/interfaces";
import { AuthStorageKeys, SerializabledDataType } from "@nts/std/types";
import { TenantType, TokenType } from "@nts/std/types";
import { EnterpriseDataDto } from "./dto/enterprise-data.dto";

@Injectable({
    providedIn: 'root'
})
export class AuthenticationTokenService {

    private onAccessTokenRetrieved = new Subject<string>();

    /**
     * Recupera l'enterprise corrente salvata nella session per il tenant corrente
     *
     * @param tenantId
     * @returns
     */
    async getEnterpriseData(tenantId: number): Promise<EnterpriseDataDto | null> {
        if (tenantId == null || tenantId === 0) {
            return Promise.resolve<EnterpriseDataDto | null>(null);
        }
        try {
            return Promise.resolve<EnterpriseDataDto | null>(JSON.parse(window.sessionStorage.getItem(`enterpriseData_${tenantId}`)));
        } catch (err) {
            return Promise.resolve<EnterpriseDataDto | null>(null);
        }
    }

    /**
     * 
     * @param accessToken 
     * @returns 
     */
    async getTenantId(accessToken: string = null): Promise<number> {
        accessToken = accessToken ?? await this.getAccessToken();
        const parsedAccessToken = this.decodeToken(accessToken);
        return parseInt(parsedAccessToken?.Tnt, 10) || 0;
    }

    async getUserName(accessToken: string = null): Promise<string> {
        accessToken = accessToken ?? await this.getAccessToken();
        const parsedAccessToken = this.decodeToken(accessToken);
        return parsedAccessToken?.family_name + ' ' + parsedAccessToken?.given_name;
    }

    async getUserId(accessToken: string = null): Promise<number> {
        accessToken = accessToken ?? await this.getAccessToken();
        const parsedAccessToken = this.decodeToken(accessToken);
        return parseInt(parsedAccessToken?.sub, 10) || 0;
    }

    async tokenHasTenantId(): Promise<boolean> {
        const accessToken = await this.getAccessToken();
        if (accessToken) {
            const parsedAccessToken = this.decodeToken(accessToken);
            return parsedAccessToken?.Tnt != null;
        }
        return false;
    }

    async storeAccessToken(newAccessToken: string): Promise<boolean> {
        return await LocalstorageHelper.setStorageItem({
            key: AuthStorageKeys.ACCESS_TOKEN_KEY,
            value: newAccessToken,
            tenantBarrier: false,
            enterpriseBarrier: false
        });
    }

    async setAccessToken(newAccessToken: string): Promise<void> {
        if (newAccessToken != null) {
            await this.onAccessTokenBeforeChanged(newAccessToken);
            await this.storeAccessToken(newAccessToken);
            LocalstorageHelper.setCurrentUserId(await this.getCurrentUserId(newAccessToken));
        }
    }

    async retrieveRefreshToken(): Promise<SerializabledDataType> {
        return await LocalstorageHelper.getStorageItem({
            key: AuthStorageKeys.REFRESH_TOKEN_KEY,
            tenantBarrier: false,
            enterpriseBarrier: false
        });
    }

    async retrieveTenantProfile(): Promise<Record<string, any>> {
        return await LocalstorageHelper.getStorageItem({
            key: AuthStorageKeys.TENANT_PROFILE_KEY,
            enterpriseBarrier: false
        }) as Record<string, any>;
    }

    async setEnterpriseData(enterpriseData: EnterpriseDataDto, tenantId: number): Promise<void> {
        window.sessionStorage.setItem(`enterpriseData_${tenantId}`, JSON.stringify(enterpriseData));

        LocalstorageHelper.setCurrentEnterpriseId(enterpriseData.enterpriseId);
        LocalstorageHelper.setCurrentCompanyId(enterpriseData.companyId);

        await LocalstorageHelper.setStorageItem({
            key: AuthStorageKeys.LAST_USED_ENTERPRISE_DATA_KEY,
            value: enterpriseData,
            enterpriseBarrier: false
        });
    }

    async clearEnterpriseData(tenantId: number): Promise<void> {
        window.sessionStorage.removeItem(`enterpriseData_${tenantId}`);

        LocalstorageHelper.setCurrentEnterpriseId(0);
        LocalstorageHelper.setCurrentCompanyId(0);

        await LocalstorageHelper.removeStorageItem({
            key: AuthStorageKeys.LAST_USED_ENTERPRISE_DATA_KEY,
            enterpriseBarrier: false
        });
    }

    async storeUserProfile(userProfile: Record<string, any>) {
        await LocalstorageHelper.setStorageItem({
            key: AuthStorageKeys.USER_PROFILE_KEY,
            value: userProfile,
            tenantBarrier: false,
            enterpriseBarrier: false
        });
    }

    async retrieveUserProfile(): Promise<Record<string, any>> {
        return await LocalstorageHelper.getStorageItem({
            key: AuthStorageKeys.USER_PROFILE_KEY,
            tenantBarrier: false,
            enterpriseBarrier: false
        }) as Record<string, any>;
    }

    async storeTenantProfile(tenantProfile: Record<string, any>): Promise<boolean> {
        return await LocalstorageHelper.setStorageItem({
            key: AuthStorageKeys.TENANT_PROFILE_KEY,
            value: tenantProfile,
            enterpriseBarrier: false
        });
    }

    async storeRefreshToken(newRefreshToken: string): Promise<boolean> {
        return LocalstorageHelper.setStorageItem({
            key: AuthStorageKeys.REFRESH_TOKEN_KEY,
            value: newRefreshToken,
            tenantBarrier: false,
            enterpriseBarrier: false
        });
    }

    getTokenExpireInMinute(token: DecodedTokenInterface): number {
        const now = new Date();
        const expMinutesToken = (((token.exp * 1000) - now.getTime()) / 60000);
        return expMinutesToken;
    }

    isTokenValid(token: string): boolean {
        if (token === '' || token == null || !JwtHelper.verifyHeader(token)) {
            return false;
        } else {
            const expTime = this.getExpirationTime(token);
            return expTime ? expTime > new Date() : false;
        }
    }

    private getExpirationTime(token: string): Date | boolean {
        const parsedToken = this.decodeToken(token);
        return parsedToken ? new Date((parsedToken.exp * 1000)) : false;
    }

    private async onAccessTokenBeforeChanged(newAccessToken: string) {
        // Se il token è valido aggiorno il localstoragehelper
        if (newAccessToken?.length > 0 && this.checkTokenType(newAccessToken, TokenType.LoginSucceeded)) {
            if (this.mockMode) {
                LocalstorageHelper.setCurrentTenantId(1);
            } else {
                const decodedToken = this.decodeToken(newAccessToken);
                if (decodedToken) {
                    LocalstorageHelper.setCurrentTenantId(parseInt(decodedToken?.Tnt));
                }
            }
        }
        return Promise.resolve();
    }

    decodeToken(token: string): DecodedTokenInterface | null {
        if (!token || token?.length === 0) {
            return null;
        }
        let decoded: DecodedTokenInterface = null;
        try {
            decoded = JwtHelper.decodeToken(token);
        } catch (e) {
            LogService.warn(e);
        }
        return decoded;
    }

    /**
     * Recupera l'access tokene presente nello storage.
     * Dopo averlo recuperato emette il Subject onAccessTokenRetrieved
     *
     * @returns l'access token presente nello storage
     */
    async getAccessToken(): Promise<string> {
        const token = await this.retrieveAccessToken() as string;
        if (token) {
            LocalstorageHelper.setCurrentUserId(await this.getCurrentUserId(token));
        }
        this.onAccessTokenRetrieved.next(token);
        return token;
    }

    async getCurrentUserId(token?: string): Promise<number> {
        if (this.mockMode) {
            return 1;
        }
        token = token || await this.getAccessToken();
        const parsedToken = this.decodeToken(token);
        return parsedToken ? parseInt(parsedToken.sub, 10) : 0;
    }

    /**
     * Recupera l'access token
     * @returns access token
     */
    async retrieveAccessToken(): Promise<SerializabledDataType> {
        return await LocalstorageHelper.getStorageItem({
            key: AuthStorageKeys.ACCESS_TOKEN_KEY,
            tenantBarrier: false,
            enterpriseBarrier: false
        });
    }

    async clearAuthData(): Promise<void> {
        await LocalstorageHelper.removeStorageItem({
            key: AuthStorageKeys.ACCESS_TOKEN_KEY,
            tenantBarrier: false,
            enterpriseBarrier: false
        });

        await LocalstorageHelper.removeStorageItem({
            key: AuthStorageKeys.REFRESH_TOKEN_KEY,
            tenantBarrier: false,
            enterpriseBarrier: false
        });

        await LocalstorageHelper.removeStorageItem({
            key: AuthStorageKeys.USER_PROFILE_KEY,
            tenantBarrier: false,
            enterpriseBarrier: false
        });

        await LocalstorageHelper.removeStorageItem({
            key: AuthStorageKeys.TENANT_PROFILE_KEY,
            enterpriseBarrier: false
        });

        LogService.clearProfiles();
    }

    checkTokenTenantType(token: string, tenantType: TenantType): boolean {
        const parsedToken = this.decodeToken(token);
        return parsedToken ? parsedToken.TntT === tenantType : false;
    }

    checkTokenType(token: string, tokenType: TokenType): boolean {
        if (this.mockMode) {
            return true;
        }
        const parsedToken: DecodedTokenInterface = this.decodeToken(token);
        return parsedToken ? parsedToken.TkT === tokenType : false;
    }

    constructor(
        @Optional()
        @Inject(AUTH_MOCK_MODE) private readonly mockMode: boolean
    ) {
        this.onAccessTokenRetrieved
            .pipe(distinctUntilChanged())
            .subscribe((accessToken: string) => {
                // Se il token è valido aggiorno il localstoragehelper
                if (accessToken?.length > 0 && this.checkTokenType(accessToken, TokenType.LoginSucceeded)) {
                    if (this.mockMode) {
                        LocalstorageHelper.setCurrentTenantId(1);
                    } else {
                        const decodedToken = this.decodeToken(accessToken);
                        if (decodedToken) {
                            LocalstorageHelper.setCurrentTenantId(parseInt(decodedToken?.Tnt));
                        }
                    }
                }
            });
    }
}